import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { t } from "@/lib/i18n";
import Header from "@/components/Header";
import StepIndicator from "@/components/StepIndicator";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import api from "@/lib/api";
import { BookOpen, Sparkles, ArrowLeft, ArrowRight, Loader2, Home } from "lucide-react";
import PolicyChat from "@/components/PolicyChat";

// Local static policies (identical to backend) so accordion renders instantly
const STATIC = {
  gaca: {
    en: [
      { title: "Passenger Rights Regulation", summary: "GACA's Passenger Protection Rights Regulation entitles passengers to compensation for flight delays over 2 hours, cancellations, denied boarding, and lost/damaged baggage." },
      { title: "Compensation for Delays", summary: "Delays 2–4 hours: refreshments + communication. Delays over 6 hours: hotel accommodation and monetary compensation up to 200% of the ticket value." },
      { title: "Baggage Complaints", summary: "Airlines must compensate for lost baggage within 21 days; damaged-baggage complaints must be filed within 7 days of receipt." },
      { title: "Filing Timeline", summary: "Complaints should first be filed directly with the airline. If unresolved within 30 days, escalate to GACA via the official portal." },
    ],
    ar: [
      { title: "لائحة حماية حقوق العملاء", summary: "تكفل الهيئة العامة للطيران المدني حقوق المسافرين بما في ذلك التعويض عن التأخير لأكثر من ساعتين، والإلغاء، ورفض الصعود، وفقدان أو تلف الأمتعة." },
      { title: "التعويض عن التأخير", summary: "تأخير من 2 إلى 4 ساعات: مرطبات ووسائل تواصل. تأخير يزيد عن 6 ساعات: إقامة فندقية وتعويض مالي يصل إلى 200٪ من قيمة التذكرة." },
      { title: "شكاوى الأمتعة", summary: "يجب على شركات الطيران التعويض عن الأمتعة المفقودة خلال 21 يومًا؛ شكاوى الأمتعة التالفة تُقدَّم خلال 7 أيام من الاستلام." },
      { title: "مهلة تقديم الشكوى", summary: "قدّم شكواك أولاً مباشرةً إلى شركة الطيران. إذا لم تُحل خلال 30 يومًا، ارفعها إلى الهيئة عبر البوابة الرسمية." },
    ],
  },
  cst: {
    en: [
      { title: "Consumer Protection Rules", summary: "The Communications, Space & Technology Commission (CST) enforces consumer protection rules covering billing accuracy, service quality, and contract transparency for telecom operators." },
      { title: "Service Quality Standards", summary: "Operators must maintain minimum coverage, call-drop, and internet-speed thresholds. Persistent violations entitle subscribers to bill adjustments." },
      { title: "Billing Disputes", summary: "Subscribers may dispute charges within 90 days. The operator must respond within 5 working days; escalate to CST if unresolved." },
      { title: "Escalation Path", summary: "File first with the operator's customer care and obtain a ticket number. If no resolution in 5 business days, submit to CST via cst.gov.sa." },
    ],
    ar: [
      { title: "قواعد حماية المستهلك", summary: "تُطبِّق هيئة الاتصالات والفضاء والتقنية قواعد حماية المستهلك التي تشمل دقة الفوترة، وجودة الخدمة، ووضوح العقود لدى مشغلي الاتصالات." },
      { title: "معايير جودة الخدمة", summary: "على المشغلين الالتزام بحدود التغطية، وسقوط المكالمات، وسرعة الإنترنت. المخالفات المتكررة تعطي المشترك حق تعديل الفاتورة." },
      { title: "نزاعات الفوترة", summary: "يحق للمشترك الاعتراض على الفواتير خلال 90 يومًا. على المشغل الرد خلال 5 أيام عمل، وإلا تُرفع الشكوى إلى الهيئة." },
      { title: "مسار التصعيد", summary: "قدّم الشكوى أولاً لخدمة عملاء المشغل واحصل على رقم بلاغ. إن لم تُحلّ خلال 5 أيام عمل، ارفعها إلى الهيئة عبر cst.gov.sa." },
    ],
  },
  tga: {
    en: [
      { title: "Postal & Parcel Service Regulation", summary: "The Transport General Authority (TGA) supervises licensed parcel carriers, ensuring safe delivery, accurate tracking, and compensation for lost or damaged shipments." },
      { title: "Delivery Time Standards", summary: "Domestic parcels: 1–3 business days within cities, up to 5 business days between regions. International: as declared by the carrier." },
      { title: "Lost or Damaged Parcels", summary: "Carriers must compensate for lost parcels based on declared value or a minimum statutory amount. Damage claims must be filed within 7 days of delivery." },
      { title: "Complaint Process", summary: "Submit first to the carrier. If unresolved within 15 days, file with TGA at tga.gov.sa attaching the carrier's response or ticket number." },
    ],
    ar: [
      { title: "لائحة خدمات البريد والطرود", summary: "تشرف الهيئة العامة للنقل على شركات نقل الطرود المرخصة لضمان التسليم الآمن، والتتبع الدقيق، والتعويض عن الشحنات المفقودة أو التالفة." },
      { title: "معايير وقت التسليم", summary: "الطرود المحلية: من 1 إلى 3 أيام عمل داخل المدن، وحتى 5 أيام عمل بين المناطق. الشحنات الدولية: حسب ما تعلنه الشركة." },
      { title: "الطرود المفقودة أو التالفة", summary: "تلتزم الشركات بالتعويض عن الطرود المفقودة وفقًا للقيمة المُعلنة أو الحد الأدنى النظامي. شكاوى التلف تُقدَّم خلال 7 أيام من الاستلام." },
      { title: "إجراءات الشكوى", summary: "قدّم الشكوى أولاً إلى الشركة. إن لم تُحلّ خلال 15 يومًا، ارفعها إلى الهيئة عبر tga.gov.sa مع إرفاق رد الشركة أو رقم البلاغ." },
    ],
  },
};

const PoliciesPage = () => {
  const { language, category, originalText, resetWizard } = useApp();
  const navigate = useNavigate();
  const [aiExplanation, setAiExplanation] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!language || !category) return;
    setLoading(true);
    api.post("/ai/policies", { text: originalText || "", category, language })
      .then((r) => setAiExplanation(r.data.ai_explanation || ""))
      .catch(() => setAiExplanation(""))
      .finally(() => setLoading(false));
  }, [language, category, originalText]);

  if (!language) return <Navigate to="/" replace />;
  if (!category) return <Navigate to="/category" replace />;

  const BackArrow = language === "ar" ? ArrowRight : ArrowLeft;
  const staticPolicies = STATIC[category][language];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <StepIndicator current={5} />
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="mt-8 mb-8 text-start"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-3">
            {t(language, "policiesTitle")}
          </h1>
          <p className="text-slate-500 text-base">{t(language, "policiesSub")}</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="h-5 w-5 text-[#006C35]" />
              <h2 className="text-lg font-semibold text-slate-900 text-start">{t(language, "keyRegulations")}</h2>
            </div>
            <Accordion type="single" collapsible defaultValue="p0" className="w-full">
              {staticPolicies.map((p, i) => (
                <AccordionItem key={i} value={`p${i}`} data-testid={`policy-item-${i}`}>
                  <AccordionTrigger className="text-start text-sm font-medium hover:text-[#006C35] hover:no-underline">
                    {p.title}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-slate-600 leading-relaxed text-start">
                    {p.summary}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <div className="bg-[#F0FDF4] border border-green-200 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-6 w-6 rounded-full bg-[#006C35] flex items-center justify-center">
                <Sparkles className="h-3.5 w-3.5 text-white" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900 text-start">
                {t(language, "tailoredExplanation")}
              </h2>
            </div>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t(language, "generating")}
              </div>
            ) : (
              <div
                data-testid="policy-ai-explanation"
                className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap text-start"
                dir={language === "ar" ? "rtl" : "ltr"}
              >
                {aiExplanation}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6">
          <PolicyChat />
        </div>

        <div className="flex items-center justify-between mt-8 gap-3">
          <Button
            data-testid="policies-back-btn"
            variant="outline"
            onClick={() => navigate("/submit")}
            className="h-11"
          >
            <BackArrow className="h-4 w-4 me-2" />{t(language, "back")}
          </Button>
          <Button
            data-testid="policies-finish-btn"
            onClick={() => { resetWizard(); navigate("/category"); }}
            className="h-11 bg-[#006C35] hover:bg-[#004F27] text-white px-6"
          >
            <Home className="h-4 w-4 me-2" />{t(language, "finish")}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default PoliciesPage;
